// CodeMirror 6 extension helpers — JS wrapper to avoid CLJS advanced-mode
// property name mangling on CM6 API surfaces.
//
// All property access on CM6 objects uses bracket notation to prevent
// the Closure compiler from renaming them in advanced mode.

import { ViewPlugin, Decoration, EditorView } from "goog:module$node_modules$$codemirror$view$dist$index_cjs";
import { foldService, foldEffect } from "goog:module$node_modules$$codemirror$language$dist$index_cjs";
import { unifiedMergeView } from "goog:module$node_modules$$codemirror$merge$dist$index_cjs";
import { merge as diff3Merge } from "goog:module$node_modules$node_diff3$dist$diff3_iife";

// ── Indent guides (EDN tab) ───────────────────────────────────────────

/**
 * Build a DecorationSet for indent guides: for each line with >= 2 leading
 * spaces, add a line decoration with class "cm-indent-guide" and an inline
 * background-size style matching the indent width.
 */
function buildIndentGuideDecorations(view) {
  var doc = view["state"]["doc"];
  var ranges = [];
  for (var pos = 0; pos < doc["length"]; ) {
    var line = doc["lineAt"](pos);
    var text = line["text"];
    var indent = 0;
    while (indent < text.length && text[indent] === " ") indent++;
    if (indent >= 2) {
      var deco = Decoration["line"]({
        "class": "cm-indent-guide",
        "attributes": { "style": "background-size:" + indent + "ch 100%" },
      });
      ranges.push(deco["range"](line["from"]));
    }
    pos = line["to"] + 1;
  }
  return Decoration["set"](ranges, true);
}

/**
 * Create the indent-guide ViewPlugin extension.
 * Recomputes decorations on doc/viewport change.
 */
export function createIndentGuidePlugin() {
  return ViewPlugin["fromClass"](
    function (view) {
      this["decorations"] = buildIndentGuideDecorations(view);
      this["update"] = function (update) {
        if (update["docChanged"] || update["viewportChanged"]) {
          this["decorations"] = buildIndentGuideDecorations(update["view"]);
        }
      };
    },
    { "decorations": function (v) { return v["decorations"]; } },
  );
}

/**
 * Create the indent-guide CSS theme extension.
 */
export function createIndentGuideTheme() {
  return EditorView["theme"]({
    ".cm-indent-guide": {
      "backgroundImage":
        "repeating-linear-gradient(to right, transparent 0, transparent calc(2ch - 1px), var(--border) calc(2ch - 1px), var(--border) 2ch)",
      "backgroundRepeat": "no-repeat",
    },
  });
}

// ── Widget-property folding (indent tab) ──────────────────────────────

function widgetHeaderLine(text) {
  var trimmed = text["trimStart"]();
  return trimmed["startsWith"](":widget ") || trimmed["startsWith"]("- :widget ");
}

function lineIndentSpaces(text) {
  var n = 0;
  while (n < text.length && text[n] === " ") n++;
  return n;
}

function findFoldEnd(doc, startPos, headerIndent, propIndent) {
  var pos = startPos;
  while (pos <= doc["length"]) {
    var line = doc["lineAt"](pos);
    var text = line["text"];
    var trimmed = text["trimStart"]();
    var indent = lineIndentSpaces(text);
    if (text["trim"]() === "" || trimmed["startsWith"](";;")) {
      pos = line["to"] + 1;
      continue;
    }
    if (indent === propIndent && trimmed["startsWith"](":children")) {
      return line["from"];
    }
    if (indent < propIndent) {
      return line["from"];
    }
    pos = line["to"] + 1;
  }
  return doc["length"];
}

/**
 * Fold service function for the indent format.
 * Returns {from, to} for foldable widget-property ranges, or null.
 */
export function indentFoldFn(state, lineStart, lineEnd) {
  var doc = state["doc"];
  var line = doc["lineAt"](lineStart);
  var text = line["text"];
  if (!widgetHeaderLine(text)) return null;
  var indent = lineIndentSpaces(text);
  var trimmed = text["trimStart"]();
  var nested = trimmed["startsWith"]("- ");
  var propIndent = nested ? indent + 2 : indent;
  var endPos = findFoldEnd(doc, line["to"] + 1, indent, propIndent);
  if (endPos <= lineEnd) return null;
  return { "from": lineEnd, "to": endPos };
}

/**
 * Create the fold service facet entry for the indent format.
 */
export function createIndentFoldService() {
  return foldService["of"](indentFoldFn);
}

/**
 * Scan the document for all widget header lines and fold their property
 * ranges. Called on editor creation for the indent format.
 */
export function applyDefaultFolds(view) {
  var doc = view["state"]["doc"];
  var effects = [];
  for (var pos = 0; pos < doc["length"]; ) {
    var line = doc["lineAt"](pos);
    var text = line["text"];
    if (widgetHeaderLine(text)) {
      var indent = lineIndentSpaces(text);
      var trimmed = text["trimStart"]();
      var nested = trimmed["startsWith"]("- ");
      var propIndent = nested ? indent + 2 : indent;
      var endPos = findFoldEnd(doc, line["to"] + 1, indent, propIndent);
      if (endPos > line["to"]) {
        effects.push(foldEffect["of"]({ "from": line["to"], "to": endPos }));
      }
    }
    pos = line["to"] + 1;
  }
  if (effects.length > 0) {
    view["dispatch"]({ "effects": effects });
  }
}

// ── Clickable ids inside a source file (F-graph-ui-v0-011-01) ─────────
//
// Every Clojure namespace in this project opens with its @adr / @feature
// annotations and every DSL model names its :handled-by. Those sentences are
// already written and completely inert. This makes them navigate.
//
// WHICH strings are linkable is decided by the SERVER and arrives as `links`
// ([{text, frameId}]). Only the graph knows whether an id names a frame, and
// asking per identifier from the browser would be one round trip each.

/**
 * Every occurrence of each link's text in the visible document, as a
 * DecorationSet of marks. Longest text first, so that an id which is a prefix
 * of another (ADR-x-01 inside ADR-x-01-slug) cannot claim the longer one's
 * span; overlapping matches are skipped rather than nested, because CodeMirror
 * requires mark ranges in a set to be non-overlapping and sorted.
 */
function buildIdLinkDecorations(view, links) {
  var text = view["state"]["doc"]["toString"]();
  var sorted = links.slice().sort(function (a, b) {
    return b["text"].length - a["text"].length;
  });
  var taken = [];
  var ranges = [];
  for (var i = 0; i < sorted.length; i++) {
    var needle = sorted[i]["text"];
    if (!needle) continue;
    var from = text.indexOf(needle);
    while (from !== -1) {
      var to = from + needle.length;
      var clash = false;
      for (var t = 0; t < taken.length; t++) {
        if (from < taken[t][1] && taken[t][0] < to) { clash = true; break; }
      }
      if (!clash) {
        taken.push([from, to]);
        ranges.push(
          Decoration["mark"]({
            "class": "cm-id-link",
            "attributes": { "data-frame-id": sorted[i]["frameId"] },
          })["range"](from, to)
        );
      }
      from = text.indexOf(needle, to);
    }
  }
  ranges.sort(function (a, b) { return a["from"] - b["from"]; });
  return Decoration["set"](ranges, true);
}

/**
 * The id-link extension: marks + a mousedown handler that reports the clicked
 * frame id. The handler is on the EDITOR rather than per mark, because a
 * decoration's DOM is rebuilt on every viewport change and listeners attached
 * to it would leak with it.
 */
export function createIdLinkPlugin(links, onClick) {
  var plugin = ViewPlugin["fromClass"](
    function (view) {
      this["decorations"] = buildIdLinkDecorations(view, links);
      this["update"] = function (update) {
        if (update["docChanged"] || update["viewportChanged"]) {
          this["decorations"] = buildIdLinkDecorations(update["view"], links);
        }
      };
    },
    { "decorations": function (v) { return v["decorations"]; } }
  );
  var handler = EditorView["domEventHandlers"]({
    "mousedown": function (event) {
      var el = event["target"];
      if (el && el["closest"]) {
        var hit = el["closest"](".cm-id-link");
        if (hit) {
          event["preventDefault"]();
          onClick(hit["getAttribute"]("data-frame-id"));
          return true;
        }
      }
      return false;
    },
  });
  return [plugin, handler];
}

// ── Three-way conflict: what is on disk, against what a person typed ──
//
// George, 2026-08-26: "my expectation is to be able to see the changes and
// merge them or select theirs or mine".
//
// unifiedMergeView shows one editor. The DOC is the person's own text and
// `original` is what arrived, so each changed chunk gets its own pair of
// controls:
//
//   accept  keeps the person's version of that chunk
//   reject  takes the incoming version of that chunk
//
// mergeControls is left at its default (true) deliberately — per-chunk choice IS
// the feature, and a whole-file answer alone is what made the first attempt at
// this useless.
//
// collapseUnchanged keeps a long file readable: a plan document is hundreds of
// lines and a conflict is usually a paragraph.
export function createUnifiedMerge(originalText) {
  return unifiedMergeView({
    "original": originalText || "",
    "mergeControls": true,
    "gutter": true,
    "highlightChanges": true,
    "allowInlineDiffs": true,
    "collapseUnchanged": { "margin": 3, "minSize": 4 },
  });
}

// ── A real three-way union merge ──────────────────────────────────────
//
// George, 2026-08-26, on the whole-file answers: they "should not be buttons
// that fire irreversibly but be radiobuttons, checkboxes — something that can be
// toggled back and forth so user can observe changing text".
//
// A toggle can only be free if every answer is DERIVED from the three sides,
// never applied to the buffer. So this returns a string and keeps no state:
// pass the same arguments again and get the same text.
//
// `Both` is a union merge against the BASE, not a concatenation. Concatenating
// two versions of one document duplicates every unchanged line, which would look
// like a broken tool rather than a choice.
//
// Returns a plain string so nothing about node-diff3's own object shape crosses
// into CLJS, where advanced compilation would rename its keys.
export function unionMerge(baseText, mineText, theirsText) {
  var base = String(baseText || "").split("\n");
  var mine = String(mineText || "").split("\n");
  var theirs = String(theirsText || "").split("\n");
  var out = diff3Merge(mine, base, theirs);
  return out["result"].join("\n");
}

// True when the two sides changed the same lines, so a person has something to
// decide. Asked separately from unionMerge because the COUNT is what makes Save
// knowable, and it must not depend on having applied anything.
export function hasTrueConflict(baseText, mineText, theirsText) {
  var base = String(baseText || "").split("\n");
  var mine = String(mineText || "").split("\n");
  var theirs = String(theirsText || "").split("\n");
  return Boolean(diff3Merge(mine, base, theirs)["conflict"]);
}


// ── Line wrapping (George, 2026-10-05: "togle wrap long lines") ──────────
//
// A function and not the bare value, for the reason every extension here is
// built in JS: advanced compilation must not rename a property it cannot see.
// With it, a line longer than the editor continues on the next screen line.
// The text itself is not changed.
export function lineWrapping() {
  return EditorView.lineWrapping;
}
